import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { checkPlayable } from "../../../../../../common/parameters";
import { IScore, TScoreData } from "../../../../../../common/types";
import { post } from "../../../../../actions/general";
import { editTask } from "../../../../../store/plan/actions";
import { IProps } from "./interfaces";

const useExerciseEditor = ({ plan, inlineEditorOpen, taskIndex }: IProps) => {
    const [openChanger, setOpenChanger] = useState<string | undefined>(undefined);
    const [playable, setPlayable] = useState<boolean>(false);
    const score = useRef<IScore | undefined>(null);
    const popup = useRef<HTMLDivElement | undefined>(null);
    const dispatch = useDispatch();
    const inlineEditorOpenRef = useRef(inlineEditorOpen);
    const checkPlayableInterval = useRef<ReturnType<typeof setInterval>>(null);

    useEffect(() => {
        inlineEditorOpenRef.current = inlineEditorOpen;
    }, [inlineEditorOpen]);

    const handleCheckPlayable = useCallback(() => {
        if (!inlineEditorOpenRef.current) return;
        const isPlayable = checkPlayable(
            {
                id: 0,
                type: plan.tasks[taskIndex].type,
                data: {
                    score: score.current,
                    color: undefined,
                },
            },
            plan
        );
        setPlayable(isPlayable);
    }, [playable]);

    const handleIsClickedOutside = useCallback((e: MouseEvent) => {
        if (!popup.current || !popup.current.contains(e.target as Node)) {
            setOpenChanger(undefined);
            window.removeEventListener("click", handleIsClickedOutside);
        }
    }, []);
    useEffect(() => {
        checkPlayableInterval.current = setInterval(handleCheckPlayable, 800);
        return () => {
            window.removeEventListener("click", handleIsClickedOutside);
            setOpenChanger(undefined);
            clearInterval(checkPlayableInterval.current);
        }
    }, []);

    const changeTempoStop = useCallback(() => {
        const { tempo = 60 } = plan.tasks[taskIndex].data as TScoreData;
        dispatch(post("task-tempo", {
            planId: plan._id,
            index: taskIndex,
            tempo,
        }));
    }, []);

    const exportScore = useCallback(() => {
        dispatch(editTask({ taskIndex, changingProp: "score", val: score.current }));
        dispatch(post("task-score", {
            planId: plan._id,
            index: taskIndex,
            score: score.current,
        }));
        changeTempoStop();
    }, []);

    const storeScore = useCallback((storedScore: IScore) => {
        score.current = storedScore;
    }, []);

    const changeTempo = useCallback((val: number) => {
        dispatch(editTask({ taskIndex, changingProp: "tempo", val }));
    }, []);

    const handleChangeOpen = (changer: string) => {
        setTimeout(() => {
            if (openChanger === changer) {
                window.removeEventListener("click", handleIsClickedOutside);
                setOpenChanger(undefined);
            } else {
                setOpenChanger(changer);
                setTimeout(() => {
                    window.addEventListener("click", handleIsClickedOutside);
                });
            }
        });
    };
    const setPopupRef = useCallback((t: HTMLDivElement) => {
        popup.current = t;
    }, []);

    return {
        openChanger,
        playable,
        exportScore,
        storeScore,
        changeTempo,
        handleChangeOpen,
        setPopupRef,
        setPlayable,
        score,
    }
};


export default useExerciseEditor;
